import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { approverApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Badge from '../../components/ui/Badge.jsx';
import Modal from '../../components/ui/Modal.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';
import { Inbox, ChevronRight, ChevronDown, Layers } from 'lucide-react';
import { formatDays } from '@/lib/utils';

// Short format (no year) for same-year dates; long (with year) for ranges
// that cross calendar boundaries. Approvers glance at this on the queue card
// — need the year visible when it matters, omitted when it's noise.
function formatDate(d, { withYear = false } = {}) {
  if (!d) return '\u2014';
  try {
    const opts = withYear
      ? { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }
      : { weekday: 'short', month: 'short', day: 'numeric' };
    return new Date(d + 'T00:00:00').toLocaleDateString('en-US', opts);
  } catch { return d; }
}

function formatDateRange(start, end) {
  if (!start || !end) return '\u2014';
  const thisYear = String(new Date().getFullYear());
  const startYear = start.slice(0, 4);
  const endYear = end.slice(0, 4);
  const withYear = startYear !== thisYear || endYear !== thisYear || startYear !== endYear;
  // One day is one date, not a range.
  if (start === end) return formatDate(start, { withYear });
  return `${formatDate(start, { withYear })} \u2192 ${formatDate(end, { withYear })}`;
}

// Group the flat queue into planner "carts". Requests submitted together from
// the Planner share a batchId (set at submit time — see my-requests-submit).
// Without this the approver sees one card per day, so a ten-day cart is ten
// separate approvals. Requests submitted before batchId existed, and one-off
// submissions, have no batchId and stay as individual cards.
//
// Group order follows the first item's position, so the API's FIFO ordering
// (oldest first) is preserved.
function groupQueue(queue) {
  const groups = [];
  const byBatch = new Map();

  for (const r of queue) {
    const bid = r.batchId;
    if (!bid) {
      groups.push({ key: r.rowKey, items: [r], isBatch: false });
      continue;
    }
    if (!byBatch.has(bid)) {
      const g = { key: `batch:${bid}`, items: [], isBatch: true };
      byBatch.set(bid, g);
      groups.push(g);
    }
    byBatch.get(bid).items.push(r);
  }

  // A cart with only one item left awaiting this approver isn't worth the
  // grouped treatment — render it as a normal card.
  for (const g of groups) if (g.items.length === 1) g.isBatch = false;
  return groups;
}

// Headline facts for a grouped cart: total days, the leave types involved, and
// the overall span. A cart can mix leave types (the Planner allows a different
// type per block), so types is a list, not a single value.
function batchSummary(items) {
  const days = items.reduce((sum, r) => sum + (Number(r.days) || 0), 0);
  const types = [...new Set(items.map((r) => r.typeName || r.typeId).filter(Boolean))];
  const starts = items.map((r) => r.startDate).filter(Boolean).sort();
  const ends = items.map((r) => r.endDate).filter(Boolean).sort();
  return { days, types, first: starts[0], last: ends[ends.length - 1] };
}

export default function ApprovalQueuePage() {
  const toast = useToast();
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState(null);
  const [expanded, setExpanded] = useState({});

  async function load() {
    setLoading(true);
    try {
      const res = await approverApi.getQueue();
      setQueue(res.queue || []);
    } catch (err) {
      toast.error(`Failed to load queue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-muted-foreground">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
        <span className="ml-3 text-sm">Loading...</span>
      </div>
    );
  }

  const groups = groupQueue(queue);

  return (
    <div>
      <h1 className="text-2xl font-bold tracking-tight">Approval Queue</h1>
      <p className="mb-6 text-muted-foreground">
        {queue.length} request{queue.length !== 1 ? 's' : ''} awaiting your approval
        {groups.some((g) => g.isBatch) && ` · grouped into ${groups.length} item${groups.length !== 1 ? 's' : ''}`}
      </p>

      {queue.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-border p-12 text-center">
          <Inbox className="mb-3 h-10 w-10 text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">All caught up! No pending approvals.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {groups.map((g) => g.isBatch ? (
            <BatchCard
              key={g.key}
              items={g.items}
              expanded={!!expanded[g.key]}
              onToggle={() => setExpanded((p) => ({ ...p, [g.key]: !p[g.key] }))}
              onAction={setAction}
            />
          ) : (
            (() => { const r = g.items[0]; return (
            <Card key={r.rowKey} className="transition-colors hover:border-primary/30">
              {/* Whole card is a link to review details — Approve/Reject
                  buttons stopPropagation so they don't also navigate. This
                  makes the "View details" metadata link redundant. */}
              <Link
                to={`/approvals/${encodeURIComponent(r.partitionKey)}/${encodeURIComponent(r.rowKey)}`}
                className="block no-underline text-inherit"
              >
                <CardContent className="p-5">
                  {/* Grid so action buttons vertically center against content
                      instead of floating above on tall cards. */}
                  <div className="grid grid-cols-[1fr_auto] items-center gap-4">
                    <div className="min-w-0">
                      <div className="mb-1 text-[15px] font-semibold">
                        {r.requesterName || r.requesterEmail}
                      </div>
                      <div className="mb-2 text-sm text-muted-foreground">
                        {r.requesterDepartment || '\u2014'}
                      </div>
                      {/* Date range on its own line so a long leave-type
                          badge below never pushes it out of view. */}
                      <div className="mb-2 text-sm font-medium">
                        {formatDateRange(r.startDate, r.endDate)}
                        <span className="ml-2 text-muted-foreground font-normal">
                          &middot; {formatDays(r.days)} day{r.days !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                        <Badge variant="info">{r.typeName || r.typeId}</Badge>
                      </div>
                      {r.reason && (
                        <div className="mt-1.5 text-sm italic text-muted-foreground">
                          &ldquo;{r.reason}&rdquo;
                        </div>
                      )}
                    </div>
                    <div
                      className="flex shrink-0 gap-2"
                      onClick={(e) => e.preventDefault()}
                    >
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={(e) => { e.preventDefault(); setAction({ type: 'reject', requests: [r] }); }}
                      >
                        Reject
                      </Button>
                      <Button
                        size="sm"
                        onClick={(e) => { e.preventDefault(); setAction({ type: 'approve', requests: [r] }); }}
                      >
                        Approve
                      </Button>
                    </div>
                  </div>
                  <div className="mt-2 text-[11px] text-muted-foreground">
                    Submitted {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString() : '\u2014'} &middot;{' '}
                    Step {(r.currentStep || 0) + 1} of {r.chainSnapshot?.length || 1}
                  </div>
                </CardContent>
              </Link>
            </Card>
            ); })()
          ))}
        </div>
      )}

      {action && (
        <ActionModal
          action={action}
          onClose={() => setAction(null)}
          onDone={async () => {
            setAction(null);
            await load();
          }}
        />
      )}
    </div>
  );
}

// One card for a whole planner cart. Collapsed it shows the totals and acts on
// every request at once; expanded it lists each day so an approver who objects
// to one of them can still handle it individually.
function BatchCard({ items, expanded, onToggle, onAction }) {
  const { days, types, first, last } = batchSummary(items);
  const r0 = items[0];

  return (
    <Card className="transition-colors hover:border-primary/30">
      <CardContent className="p-5">
        <div className="grid grid-cols-[1fr_auto] items-center gap-4">
          <div className="min-w-0">
            <div className="mb-1 flex items-center gap-2 text-[15px] font-semibold">
              {r0.requesterName || r0.requesterEmail}
              <Badge variant="warning" className="inline-flex items-center gap-1">
                <Layers className="h-3 w-3" />
                {items.length} requests
              </Badge>
            </div>
            <div className="mb-2 text-sm text-muted-foreground">
              {r0.requesterDepartment || '—'}
            </div>
            <div className="mb-2 text-sm font-medium">
              {formatDateRange(first, last)}
              <span className="ml-2 font-normal text-muted-foreground">
                &middot; {formatDays(days)} day{days !== 1 ? 's' : ''} across {items.length} requests
              </span>
            </div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              {types.map((t) => <Badge key={t} variant="info">{t}</Badge>)}
            </div>
          </div>
          <div className="flex shrink-0 gap-2">
            <Button variant="danger" size="sm" onClick={() => onAction({ type: 'reject', requests: items })}>
              Reject all
            </Button>
            <Button size="sm" onClick={() => onAction({ type: 'approve', requests: items })}>
              Approve all
            </Button>
          </div>
        </div>

        <div className="mt-2 text-[11px] text-muted-foreground">
          Submitted {r0.submittedAt ? new Date(r0.submittedAt).toLocaleDateString() : '—'} &middot;{' '}
          Step {(r0.currentStep || 0) + 1} of {r0.chainSnapshot?.length || 1}
        </div>

        {/* Deliberately styled as an obvious control. The first version used the
            same 11px muted text as the metadata line directly above it, so it
            read as more metadata and approvers never realised the cart could be
            opened to action days individually. */}
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={expanded}
          className="mt-3 flex w-full items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          {expanded ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
          {expanded ? 'Hide individual days' : `Review the ${items.length} days individually`}
          <span className="ml-auto text-xs font-normal text-muted-foreground">
            {expanded ? '' : 'approve or reject one at a time'}
          </span>
        </button>

        {expanded && (
          <div className="mt-1 flex flex-col divide-y divide-border rounded-md border border-border px-3">
            {items.map((r) => (
              <div key={r.rowKey} className="grid grid-cols-[1fr_auto] items-center gap-3 py-2.5">
                <div className="min-w-0">
                  <Link
                    to={`/approvals/${encodeURIComponent(r.partitionKey)}/${encodeURIComponent(r.rowKey)}`}
                    className="text-sm font-medium no-underline text-inherit hover:underline"
                  >
                    {formatDateRange(r.startDate, r.endDate)}
                  </Link>
                  <span className="ml-2 text-sm text-muted-foreground">
                    &middot; {formatDays(r.days)} day{r.days !== 1 ? 's' : ''} &middot; {r.typeName || r.typeId}
                  </span>
                  {r.reason && (
                    <div className="mt-0.5 text-xs italic text-muted-foreground">&ldquo;{r.reason}&rdquo;</div>
                  )}
                </div>
                <div className="flex shrink-0 gap-2">
                  <Button variant="danger" size="sm" onClick={() => onAction({ type: 'reject', requests: [r] })}>
                    Reject
                  </Button>
                  <Button size="sm" onClick={() => onAction({ type: 'approve', requests: [r] })}>
                    Approve
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ActionModal({ action, onClose, onDone }) {
  const toast = useToast();
  const isApprove = action.type === 'approve';
  const requests = action.requests || [];
  const r = requests[0];
  const many = requests.length > 1;
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);

  async function handleSubmit() {
    if (!isApprove && note.trim().length < 3) {
      toast.error('Rejection reason is required (minimum 3 characters)');
      return;
    }

    setSubmitting(true);
    setProgress(0);
    let done = 0;
    const failures = [];

    // SEQUENTIAL on purpose. The backend sends one consolidated outcome email
    // per cart, and it decides to send by checking whether any sibling request
    // is still pending. Firing these in parallel would let every call still see
    // a pending sibling, so nobody would send the email. One at a time means
    // the last call sees a fully-decided cart and sends exactly one summary.
    for (const req of requests) {
      try {
        if (isApprove) {
          await approverApi.approve(req.partitionKey, req.rowKey, { note: note.trim() || undefined });
        } else {
          await approverApi.reject(req.partitionKey, req.rowKey, { reason: note.trim() });
        }
        done++;
      } catch (err) {
        failures.push(`${req.startDate}: ${err.message}`);
      }
      setProgress(done + failures.length);
    }

    const verb = isApprove ? 'Approved' : 'Rejected';
    if (done > 0) {
      toast.success(many ? `${verb} ${done} of ${requests.length} requests` : `${verb} ${r.requesterName}'s request`);
    }
    // Report partial failure explicitly — silently dropping some of a cart
    // would leave the approver believing they cleared the whole thing.
    if (failures.length) {
      toast.error(`${failures.length} failed — ${failures.slice(0, 3).join('; ')}`);
    }

    setSubmitting(false);
    onDone();
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={
        many
          ? `${isApprove ? 'Approve' : 'Reject'} ${requests.length} requests`
          : (isApprove ? 'Approve Request' : 'Reject Request')
      }
      width="480px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button variant={isApprove ? 'primary' : 'danger'} onClick={handleSubmit} loading={submitting}>
            {submitting && many
              ? `${progress} / ${requests.length}`
              : (isApprove ? (many ? 'Approve all' : 'Approve') : (many ? 'Reject all' : 'Reject'))}
          </Button>
        </>
      }
    >
      <div className="mb-4">
        <div className="font-medium">{r.requesterName}</div>
        {many ? (
          <>
            <div className="text-sm text-muted-foreground">
              {batchSummary(requests).types.join(', ')} &middot;{' '}
              {formatDays(batchSummary(requests).days)} days across {requests.length} requests
            </div>
            <div className="mt-2 max-h-40 overflow-y-auto rounded-md border border-border bg-muted/30 p-2 text-xs text-muted-foreground">
              {requests.map((q) => (
                <div key={q.rowKey}>
                  {formatDateRange(q.startDate, q.endDate)} &middot; {formatDays(q.days)}d &middot; {q.typeName || q.typeId}
                </div>
              ))}
            </div>
            {!isApprove && (
              <div className="mt-2 text-xs text-muted-foreground">
                The same reason is applied to all {requests.length}. To reject only some,
                close this and use &ldquo;Show individual days&rdquo;.
              </div>
            )}
          </>
        ) : (
          <div className="text-sm text-muted-foreground">
            {r.typeName} &middot; {formatDays(r.days)} day{r.days !== 1 ? 's' : ''} &middot; {formatDateRange(r.startDate, r.endDate)}
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1.5">
        <label className="text-sm font-medium">
          {isApprove ? 'Note (optional)' : 'Reason for rejection (required)'}
        </label>
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          placeholder={isApprove ? 'Add an optional note...' : 'Explain why this request is being rejected...'}
          className="w-full rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          style={{ resize: 'vertical' }}
        />
      </div>
    </Modal>
  );
}
